import React, { useEffect, useState } from 'react';
import { SlideOutMenuType } from '@mm/state/interfaces/view';
import { useDispatch, useSelector } from 'react-redux';
import {
  activeChatbotIdSelector,
  selectActiveCompanyId,
} from '@mm/state/selectors/viewSelectors';
import { selectCampaignById } from '@mm/state/selectors/campaignSelectors';
import { useCampaignCRUDActions } from '@mm/state/actions/CampaignsCRUDActions';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { useSlideoutOnClose } from '../SlideoutEmail/SlideoutEmail';
import { useResolutionInfo } from '@mm/hooks';
import { MOBILE_THRESHOLD } from '../../../../constants/screenSize';
import css from './AutomationReportingSlideout.module.css';
import { Button, Heading, Switch } from '@mm/components';
import PrimarySelect, {
  DropdownOption,
  PrimarySelectOuterProps,
} from '../../../../../common/components/PrimarySelect/PrimarySelect';
import { errorToast, successToast } from '../../../../../common/helpers/toast';
import { SlideoutMenu as Menu } from '../../components/SlideoutMenu';
import {
  CloseOutlined,
  DeleteTwoTone,
  PlusCircleOutlined,
  ReconciliationOutlined,
} from '@ant-design/icons';
import classNames from 'classnames';
import automationStyles from '../../../../features/beta/modules/Automations/automationStyles.module.css';
import { AngleDownLightIcon } from '@mm/icons';
import { EmailInput } from '../../../../../common/modals/InviteUserModal/EmailInput/EmailInput';
import { randomId } from '../../../../../common/helpers/utils';
import { EMAIL_REGEX_DOT_REQUIRED } from '../../../../../common/constants/validations';
import ReportController, {
  EAutomationReportingOpts,
  ReportPayload,
  ReportType,
} from '@mm/state/controllers/ReportController';
import commonHandler from '@mm/state/errorHandlers/common-handler';

export interface IAutomationReportingSlideout {
  menuType: SlideOutMenuType;
  campaignId?: number;
  className?: string;
}

const defaultReport = {
  active: false,
  frequency: EAutomationReportingOpts.WEEKLY,
  emails: [],
};

export const REPORTING_OPTIONS = [
  {
    id: EAutomationReportingOpts.DAILY,
    name: 'Daily',
  },
  {
    id: EAutomationReportingOpts.WEEKLY,
    name: 'Weekly',
  },
  {
    id: EAutomationReportingOpts.TWICE_PER_MONTH,
    name: 'Twice per month',
  },
  {
    id: EAutomationReportingOpts.MONTHLY,
    name: 'Monthly',
  },
];

export const ReportingFrequencySelector = ({
  ...props
}: Omit<PrimarySelectOuterProps, 'options' | 'value'> & {
  value?: string | number;
}) => {
  const value = REPORTING_OPTIONS.find(
    v => props.value && v.id.toString() === props.value.toString(),
  );

  return (
    <PrimarySelect
      {...props}
      renderWithoutPopover
      className={classNames(automationStyles.select, props?.className)}
      customArrowIcon={
        <AngleDownLightIcon
          className={classNames(automationStyles.selectArrow)}
          color={props.disabled ? '#8C8C8C' : '#262626'}
        />
      }
      customOptionRenderer={opt => (
        <>
          {opt.icon}
          {opt.name}
        </>
      )}
      options={REPORTING_OPTIONS}
      value={value}
    />
  );
};

const CAP = 5;
const ids = [randomId()];

const diff = (arr1: any[] = [], arr2: any[] = []) =>
  arr1.filter(Boolean).sort().join('') !== arr2.filter(Boolean).sort().join('');

export const AutomationReportingSlideout = ({
  menuType,
  campaignId,
  className,
}: IAutomationReportingSlideout) => {
  const chatbotId = useSelector(activeChatbotIdSelector);
  const companyId = useSelector(selectActiveCompanyId);

  const campaign = useSelector(state =>
    selectCampaignById(state, campaignId || -1),
  );

  const [busy, setBusy] = useState(false);

  const campaignCRUDActions = useCampaignCRUDActions();

  const isSaveDisabled = busy;

  const modalActions = useModalActions();

  const viewActions = useViewActions();

  const { setRef, close: closeSlideout } = useSlideoutOnClose(async () => {},
  menuType);

  const companyUserIdsToNotify: number[] = [];

  const { screenWidth } = useResolutionInfo();
  const isMobile = screenWidth <= MOBILE_THRESHOLD;

  const [isActive, setIsActive] = useState<boolean>(false);

  const [frequencyValue, setFrequencyValue] =
    useState<EAutomationReportingOpts>();

  const handleEventSelectorChange = (opt: DropdownOption) => {
    setFrequencyValue(opt.id as EAutomationReportingOpts);
    onSave({ frequency: opt.id as EAutomationReportingOpts });
  };

  const onSave = async (payload?: Partial<ReportType>) => {
    setBusy(true);
    try {
      if (campaignId) {
        await updateReport(payload);
        successToast('Your changes have been saved.', 'top-left');
      }
    } catch (ee) {
      errorToast('Something went wrong', 'top-left');
    } finally {
      setBusy(false);
    }
  };

  const handleToggleChange = () => {
    setIsActive(!isActive);
    onSave({ active: !isActive });
  };

  const [dirty, setDirty] = useState(false);

  const [emails, setEmails] = useState<string[]>([]);

  const onAddEmail = () => {
    setEmails([...emails, '']);
    ids.push(randomId());
  };

  const canSubmit =
    emails.every(email =>
      EMAIL_REGEX_DOT_REQUIRED.test(email.toString().trim()),
    ) && !busy;

  const handleReject = async () => {
    close();
  };

  const [report, setReport] = useState<ReportPayload>();

  const dispatch = useDispatch();

  const syncReportState = (report: ReportPayload) => {
    setReport(report);

    setFrequencyValue(report?.frequency);

    const mails = [...(report.emails || []).filter(Boolean), ''];
    setEmails(mails);
    mails.forEach(m => ids.push(randomId()));

    setIsActive(report.active);

    setDirty(true);
  };

  const updateReport = async payload => {
    if (report) {
      const res = await ReportController.updateReport(report.id, {
        ...report,
        ...payload,
      });

      if (res.success) {
        syncReportState(res.report);
      }
    }
  };

  const getReports = async cid => {
    try {
      setBusy(true);
      const res = await ReportController.getReports(cid);

      let report = res.reports?.[0];

      if (!res.reports.length) {
        report = await ReportController.createReport(cid, defaultReport);
      }

      if (report) {
        syncReportState(report);
      }
    } catch (err) {
      console.error(err);
      await commonHandler(dispatch)(err);
    } finally {
      setBusy(false);
    }
  };

  const onEmailInputBlur = () => {
    if (diff(report?.emails, emails)) {
      onSave({ emails });
    }
  };

  const onEmailRemove = updatedEmails => {
    if (diff(report?.emails, updatedEmails)) {
      onSave({ emails: updatedEmails });
    }
  };

  useEffect(() => {
    if (campaignId && !report) {
      getReports(campaignId);
    }
  }, [campaignId]);

  return (
    <Menu className={css.container} setRef={setRef}>
      <div className={css.header}>
        <ReconciliationOutlined className={css.icon} color="#FAAD14" />
        <Heading size={3}>Reporting settings</Heading>
        <span className={css.close} onClick={closeSlideout}>
          <CloseOutlined />
        </span>
      </div>
      <Menu.Content className={css.content}>
        <Switch
          checked={isActive}
          label="Sending reports"
          disabled={busy}
          onChange={handleToggleChange}
          css={{
            container: css.switch,
            thumb: isActive ? css.thumbActive : css.thumbInactive,
            label: isActive ? css.switchLabelActive : css.switchLabelInactive,
          }}
        />

        <div className={css.emails}>
          <div className={css.block}>
            <span>Email to send reports to:</span>

            {emails.map((email, idx) => (
              <div className={css.inputRow} key={ids[idx]}>
                <EmailInput
                  strict
                  withIcon={false}
                  defaultValue={email}
                  inputProps={{
                    disabled: !isActive,
                    onBlur: onEmailInputBlur,
                    placeholder: 'Enter email',
                  }}
                  onChange={v => {
                    const dup = [...emails];
                    dup[idx] = v.email;
                    setEmails(dup);
                  }}
                />
                {!!idx && (
                  <DeleteTwoTone
                    className={css.deleteButton}
                    color="#2F54EB"
                    onClick={() => {
                      const dup = [...emails];
                      dup.splice(idx, 1);
                      ids.splice(idx, 1);
                      setEmails(dup);
                      onEmailRemove(dup);
                    }}
                  />
                )}
              </div>
            ))}
          </div>

          {emails.length < CAP && (
            <Button
              css={{ container: css.addButton }}
              color="transparent-no-shadow"
              leftIcon={<PlusCircleOutlined />}
              disabled={!isActive}
              onClick={onAddEmail}
            >
              Add one more
            </Button>
          )}

          {emails.length >= CAP && (
            <span className={css.hint}>Up to {CAP} emails</span>
          )}
        </div>

        <div className={css.selectorContainer}>
          <span>How often do you want to get a report on this Automation:</span>
          <ReportingFrequencySelector
            value={frequencyValue}
            disabled={!isActive || busy}
            onChange={handleEventSelectorChange}
          />
        </div>
      </Menu.Content>
    </Menu>
  );
};
